import { useState, useMemo, useEffect } from "react";
import { TIMESHEET_STATUSES } from "../data/data";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";
import { getTimesheets, createTimesheet, updateTimesheet, getAllProjects } from "../service/rpc";
import { useUser } from "../context/UserContext";
import toast from "react-hot-toast";
import "../styles/PageShared.css";

const ITEMS_PER_PAGE = 6;
const EMPTY_FORM = { project: "", project_id: "", task: "", date: "", hours: "" };

export default function TimesheetsPage() {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewType, setViewType] = useState("list");

  // Modal state  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [projects, setProjects] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const { uid, loading: userLoading, employeeId, partnerId, isPortal } = useUser();

  //logic of kanban view in mobile 

  useEffect(() => {
    if (window.innerWidth <= 992) {
      setViewType("grid");
    }
  }, [])

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;
        let domain = [];

        if (search.trim()) {
          const q = search.trim();
          const searchDomain = ['|', ['name', 'ilike', q], ['project_id', 'ilike', q]];
          domain = domain.length > 0 ? ['&', ...domain, ...searchDomain] : searchDomain;
        }

        const [{ timesheets: tsData, count }, { projects: projectsData }] = await Promise.all([
          getTimesheets(domain, ITEMS_PER_PAGE, offset),
          getAllProjects([], 200, 0)
        ]);

        const mappedData = tsData.map(ts => ({
          id: ts.id,
          project: ts.project_id ? ts.project_id[1] : "Unknown",
          project_id_raw: ts.project_id,
          date: (ts.date_start && typeof ts.date_start === 'string') ? ts.date_start.split(' ')[0] : (ts.create_date ? ts.create_date.split(' ')[0] : "N/A"),
          hours: ts.duration || 0,
          task: ts.name || (ts.task_id ? ts.task_id[1] : "No Description"),
          status: "Approved"
        }));
        setTimesheets(mappedData);
        setTotalItems(count);
        setProjects(projectsData);

      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Error loading timesheets. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
    if (!userLoading && uid) {
      fetchData();
    }
  }, [search, currentPage, uid, userLoading]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setModalMode("create");
    setForm({ ...EMPTY_FORM, date: today });
    setEditTarget(null);
    setFormErrors({});
    setModalOpen(true);
  };

  const openEdit = (e, ts) => {
    e.stopPropagation();
    setModalMode("edit");
    setForm({
      project: ts.employee,
      project_id: ts.project_id_raw ? ts.project_id_raw[0] : "",
      task: ts.task,
      date: ts.date,
      hours: String(ts.hours)
    });
    setEditTarget(ts);
    setModalOpen(true);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      if (!form.project_id) {
        setFormErrors({ project_id: "Project is mandatory" });
        return;
      }
      setFormErrors({});

      setSaving(true);
      const payload = {
        name: form.task,
        project_id: form.project_id ? parseInt(form.project_id) : false,
        task_id: form.task_id ? parseInt(form.task_id) : false,
        duration: parseFloat(form.hours) || 0,
        user_id: uid,
        date_start: form.date,
      };

      if (!uid) {
        toast.error("You must be logged in to save timesheets.");
        setSaving(false);
        return;
      }

      if (modalMode === "create") {
        const newId = await createTimesheet(payload);
        const selProj = projects.find(p => p.id === parseInt(form.project_id));
        setTimesheets(prev => [{
          id: newId,
          employee: selProj ? selProj.name : "Unknown",
          project_id_raw: selProj ? [selProj.id, selProj.name] : null,
          date: form.date,
          hours: parseFloat(form.hours) || 0,
          task: form.task,
          status: "Approved"
        }, ...prev]);
      } else {
        await updateTimesheet(editTarget.id, payload);
        const selProj = projects.find(p => p.id === parseInt(form.project_id));
        setTimesheets(prev => prev.map(ts => ts.id === editTarget.id
          ? {
            ...ts,
            task: form.task,
            date: form.date,
            hours: parseFloat(form.hours) || 0,
            employee: selProj ? selProj.name : ts.employee,
            project_id_raw: selProj ? [selProj.id, selProj.name] : ts.project_id_raw
          }
          : ts
        ));
      }
      setModalOpen(false);
      toast.success(modalMode === "create" ? "Timesheet logged successfully!" : "Timesheet updated successfully!");
      window.location.reload();
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const pagedData = timesheets;


  if (error) return (
    <main className="main">
      <div className="error-container" style={{ textAlign: 'center', padding: '100px 20px' }}>
        <div style={{ color: '#ef4444', fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#1e293b', marginBottom: '10px', fontFamily: 'Sora' }}>Something went wrong</h2>
        <p style={{ color: '#64748b', marginBottom: '20px' }}>{error}</p>
        <button className="btn-create" onClick={() => window.location.reload()}>Retry</button>
      </div>
    </main>
  );

  return (
    <main className="main">
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalMode === "create" ? "Log Time" : "Edit Timesheet"}
        onSave={handleSave}
        saveLabel={modalMode === "create" ? "Log Time" : "Update"}
        saving={saving}
      >
        <div className="modal-form__group">
          <label className="modal-form__label">Date</label>
          <input className="modal-form__input" type="date" name="date" value={form.date} onChange={handleFieldChange} />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Project</label>
          <select className={`modal-form__input ${formErrors.project_id ? 'modal-form__input--error' : ''}`} name="project_id" value={form.project_id} onChange={handleFieldChange}>
            <option value="">Select a project...</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {formErrors.project_id && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', fontWeight: '500' }}>{formErrors.project_id}</div>}
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Hours Spent</label>
          <input className="modal-form__input" type="number" step="0.5" name="hours" value={form.hours} onChange={handleFieldChange} placeholder="e.g. 4.5" />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Task Description</label>
          <textarea className="modal-form__input" name="task" value={form.task} onChange={handleFieldChange} placeholder="What did you work on?" rows={7} style={{ resize: 'vertical' }} />
        </div>
      </Modal>
      <div className="page-header">
        <h1 className="page-header__title">Timesheets</h1>

      </div>

      <div className="task-tab-header">
        <div className="page-header__actions">
          <button className="btn-create" onClick={openCreate}>+ New</button>
        </div>
        <SearchBar search={search} setSearch={setSearch} placeholder="Search by project or task…" style={{ flex: 1, margin: 0 }} />

        <div className="view-toggle">
          <button className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('list')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            List
          </button>
          <button className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('grid')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
            Kanban
          </button>
        </div>
      </div>

      <div className={`table-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading...</p>
          </div>
        )}
        {viewType === "list" ? (
          <table className="table">
            <thead>
              <tr>
                <th className="table__th" style={{ width: '17%' }}>Date</th>
                <th className="table__th" style={{ width: '25%' }}>Project</th>
                <th className="table__th" style={{ width: '10%' }}>Hours</th>
                <th className="table__th" style={{ width: '50%' }}>Description</th>
                <th className="table__th" style={{ width: '50px', textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedData.length === 0 ? (
                <tr><td className="table__empty" colSpan={5}>No timesheets match your filter.</td></tr>
              ) : pagedData.map((ts) => (
                <tr key={ts.id} className="table__row">
                  <td className="table__td" ><span className="table__client">{ts.date}</span></td>
                  <td className="table__td"><span className="table__name">{ts.project}</span></td>
                  <td className="table__td"><span className="table__value">{ts.hours}h</span></td>
                  <td className="table__td"><span className="table__client">{ts.task}</span></td>
                  <td className="table__td" style={{ textAlign: 'right' }}>
                    <button className="btn-row-edit" onClick={(e) => openEdit(e, ts)} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        ) : (
          <div className="task-grid">
            {pagedData.length === 0 ? (
              <div className="table__empty" style={{ padding: '40px' }}>No timesheets match your filter.</div>
            ) : pagedData.map((ts) => (
              <div key={ts.id} className="kanban-card">
                <div className="kanban-card__top" style={{ display: 'flex', justifyContent: 'flex-end' }} >
                  <div >
                    <button className="btn-row-edit" onClick={(e) => openEdit(e, ts)} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: '1.5', marginBottom: '12px' }}>{ts.task}</div>
                <div style={{ marginBottom: '16px', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>{ts.project}</div>
                <div className="kanban-card__bottom">
                  <div style={{ fontWeight: '700', color: '#1a2332' }}>{ts.date}</div>
                  <span className="kanban-card__id">{ts.hours}h</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </div>
    </main>
  );
}
